import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { money } from "@/lib/api";
import { eurEquivalent } from "@/lib/currency";
import { Navbar, CategoryIcon, Stars, VerifiedBadge, Avatar } from "@/components/shared";
import { groupCategories } from "@/lib/categories";
import { useTranslation } from "react-i18next";
import { catName, pickLang } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, MapPin, X, List, Map as MapIcon, Navigation, Loader2 } from "lucide-react";
import { ProMap } from "@/components/ProMap";
import { toast } from "sonner";

export default function Browse() {
    const [params, setParams] = useSearchParams();
    const [categories, setCategories] = useState([]);
    const [groups, setGroups] = useState([]);
    const [pros, setPros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState(params.get("q") || "");
    const [city, setCity] = useState(params.get("city") || "");
    const [view, setView] = useState("list");
    const [userLoc, setUserLoc] = useState(null);
    const [geoLoading, setGeoLoading] = useState(false);
    const [radius, setRadius] = useState(null);
    const category = params.get("category") || "";
    const { t } = useTranslation();

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
        api.get("/category-groups").then((r) => setGroups(r.data)).catch(() => {});
    }, []);

    const grouped = groupCategories(categories, groups);

    const load = useCallback(async () => {
        setLoading(true);
        const p = {};
        if (category) p.category = category;
        if (city) p.city = city;
        if (q) p.q = q;
        if (userLoc) { p.lat = userLoc.lat; p.lng = userLoc.lng; if (radius) p.radius = radius; }
        try {
            const { data } = await api.get("/professionals", { params: p });
            setPros(data);
        } finally {
            setLoading(false);
        }
    }, [category, city, q, userLoc, radius]);

    useEffect(() => { load(); }, [category, userLoc, radius]); // eslint-disable-line

    const useMyLocation = () => {
        if (!navigator.geolocation) return toast.error(t("Geolocalizzazione non supportata dal browser"));
        setGeoLoading(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                setGeoLoading(false);
                toast.success(t("Professionisti ordinati per vicinanza a te"));
            },
            () => {
                setGeoLoading(false);
                toast.error(t("Impossibile ottenere la tua posizione"));
            },
            { enableHighAccuracy: true, timeout: 8000 }
        );
    };

    const setCategory = (slug) => {
        const np = new URLSearchParams(params);
        if (slug) np.set("category", slug); else np.delete("category");
        setParams(np);
    };

    return (
        <div className="min-h-screen bg-cream">
            <Navbar />
            <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-black text-ink">{t("Trova un professionista")}</h1>
                        <p className="mt-2 text-ink/60">{t("{{count}} professionisti disponibili", { count: pros.length })}</p>
                    </div>
                    <div className="flex items-center gap-1 rounded-full border border-border bg-white p-1 self-start sm:self-auto">
                        <button onClick={() => setView("list")} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${view === "list" ? "bg-ink text-white" : "text-ink"}`} data-testid="view-list">
                            <List className="h-4 w-4" /> {t("Lista")}
                        </button>
                        <button onClick={() => setView("map")} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${view === "map" ? "bg-ink text-white" : "text-ink"}`} data-testid="view-map">
                            <MapIcon className="h-4 w-4" /> {t("Mappa")}
                        </button>
                    </div>
                </div>

                {/* filters */}
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <div className="flex items-center gap-2 rounded-full bg-white border border-border px-3 flex-1">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                            placeholder={t("Cerca per nome o competenza")} className="border-0 shadow-none focus-visible:ring-0 bg-transparent" data-testid="browse-search" />
                    </div>
                    <div className="flex items-center gap-2 rounded-full bg-white border border-border px-3 sm:w-56">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <Input value={city} onChange={(e) => setCity(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                            placeholder={t("Città")} className="border-0 shadow-none focus-visible:ring-0 bg-transparent" data-testid="browse-city" />
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:contents">
                        <Button onClick={load} className="rounded-full bg-ink hover:bg-inkhover px-8" data-testid="browse-apply">{t("Filtra")}</Button>
                        <Button onClick={useMyLocation} variant={userLoc ? "default" : "outline"}
                            className={`rounded-full px-5 ${userLoc ? "bg-terracotta hover:bg-terracottahover text-white" : "border-ink/20"}`}
                            data-testid="near-me-btn">
                            {geoLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Navigation className="h-4 w-4 mr-1" />}
                            {userLoc ? t("Vicino a te") : t("Vicino a me")}
                        </Button>
                    </div>
                </div>
                {userLoc && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="text-xs font-medium text-ink/60 mr-1">{t("Raggio:")}</span>
                        {[{ v: null, l: "Tutti" }, { v: 10, l: "10 km" }, { v: 25, l: "25 km" }, { v: 50, l: "50 km" }].map((r) => (
                            <button key={r.l} onClick={() => setRadius(r.v)}
                                className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${radius === r.v ? "bg-terracotta text-white border-terracotta" : "bg-white text-ink border-border hover:border-ink/30"}`}
                                data-testid={`radius-${r.v ?? "all"}`}>
                                {t(r.l)}
                            </button>
                        ))}
                        <button onClick={() => { setUserLoc(null); setRadius(null); }} className="ml-1 text-xs text-ink/50 hover:text-terracotta inline-flex items-center gap-1" data-testid="clear-location">
                            <X className="h-3 w-3" /> {t("Rimuovi vicinanza")}
                        </button>
                    </div>
                )}

                {/* category chips grouped by macro-category */}
                <div className="mt-5 space-y-4">
                    <button onClick={() => setCategory("")} className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${!category ? "bg-ink text-white border-ink" : "bg-white text-ink border-border hover:border-ink/30"}`} data-testid="cat-chip-all">
                        {t("Tutte le categorie")}
                    </button>
                    {grouped.map((g) => (
                        <div key={g.slug}>
                            <p className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink/40 mb-2" data-testid={`chip-group-${g.slug}`}>{catName(g)}</p>
                            <div className="flex flex-wrap gap-2">
                                {g.items.map((c) => (
                                    <button key={c.slug} onClick={() => setCategory(c.slug)}
                                        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium border transition-colors ${category === c.slug ? "bg-ink text-white border-ink" : "bg-white text-ink border-border hover:border-ink/30"}`}
                                        data-testid={`cat-chip-${c.slug}`}>
                                        <CategoryIcon name={c.icon} size={16} /> {catName(c)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* grid */}
                {view === "map" ? (
                    <div className="mt-8">
                        {pros.some((p) => p.lat == null) && (
                            <p className="mb-3 text-sm text-ink/50">{t("Alcuni professionisti non hanno una posizione sulla mappa e non sono mostrati.")}</p>
                        )}
                        <ProMap professionals={pros} />
                    </div>
                ) : loading ? (
                    <div className="py-24 flex justify-center">
                        <div className="h-8 w-8 rounded-full border-2 border-terracotta border-t-transparent animate-spin" />
                    </div>
                ) : pros.length === 0 ? (
                    <div className="py-24 text-center text-ink/50">
                        <X className="h-10 w-10 mx-auto mb-3" />
                        {t("Nessun professionista trovato con questi filtri.")}
                    </div>
                ) : (
                    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {pros.map((p) => (
                            <Link key={p.id} to={`/professionista/${p.id}`}
                                className="group rounded-2xl border border-border bg-white overflow-hidden hover:-translate-y-1 transition-transform duration-300"
                                data-testid={`pro-card-${p.id}`}>
                                <div className="aspect-[4/3] overflow-hidden">
                                    <Avatar src={p.photo_url} name={p.name} className="h-full w-full group-hover:scale-105 transition-transform duration-500" fallbackClassName="text-5xl" />
                                </div>
                                <div className="p-5">
                                    <div className="flex items-center justify-between gap-2">
                                        <h3 className="font-heading font-bold text-ink text-lg">{p.name}</h3>
                                        {p.verified && <VerifiedBadge small />}
                                    </div>
                                    <p className="text-sm text-terracotta font-medium mt-0.5">{pickLang(p.category_name, p.category_name_en)}</p>
                                    {p.distance_km != null ? (
                                        <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-ink/70" data-testid={`distance-${p.id}`}>
                                            <Navigation className="h-3 w-3 text-terracotta" /> {p.distance_km < 1 ? t("a meno di 1 km da te") : t("a {{km}} km da te", { km: p.distance_km })} · {p.city}
                                        </p>
                                    ) : (
                                        <p className="mt-1 inline-flex items-center gap-1 text-xs text-ink/50"><MapPin className="h-3 w-3" /> {p.city}</p>
                                    )}
                                    <p className="mt-2 text-sm text-ink/60 line-clamp-2">{p.bio}</p>
                                    <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                                        <Stars value={p.rating} count={p.reviews_count} />
                                        <span className="text-right">
                                            <span className="font-heading font-bold text-ink block">{money(p.hourly_rate, p.currency_symbol)}<span className="text-xs font-normal text-ink/50">/h</span></span>
                                            {eurEquivalent(p.hourly_rate, p.currency_code) != null && (
                                                <span className="text-[11px] text-ink/60" data-testid={`eq-${p.id}`}>≈ €{eurEquivalent(p.hourly_rate, p.currency_code)}/h</span>
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
